/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React from 'react';

const OPERATORS = ['>','>=','<','<=','==','!=','CROSSES_ABOVE','CROSSES_BELOW'];

// Operand Editors support three modes: CONST, REF, RAW
function classifyOperand(v) {
  if (v == null) return { kind: 'EMPTY' };
  if (typeof v === 'number') return { kind: 'CONST', value: v };
  if (typeof v === 'string') return { kind: 'REF', value: v };
  if (typeof v === 'object' && v.type === 'price') return { kind: 'RAW', value: v };
  return { kind: 'UNKNOWN', value: v };
}

function buildOperand(kind, state) {
  switch (kind) {
    case 'CONST': return state.constValue ?? 0;
    case 'REF': return state.refValue || '';
    case 'RAW': {
      const { field, timeframe, lag } = state.rawValue || { field: 'volume' };
      const base = { type:'price', field: field || 'volume' };
      if (timeframe) base.timeframe = timeframe;
      if (Number.isFinite(lag) && lag > 0) base.lag = lag;
      return base;
    }
    default: return '';
  }
}

export default function ComparisonEditor({ comparison, onChange, availableSeries }) {
  if (!comparison) return <div className="text-xs text-textSecondary">No comparison selected.</div>;

  // Local derived operand states
  const lhsMeta = classifyOperand(comparison.lhs);
  const rhsMeta = classifyOperand(comparison.rhs);

  const [lhsMode, setLhsMode] = React.useState(lhsMeta.kind === 'EMPTY' ? 'REF' : (lhsMeta.kind === 'UNKNOWN' ? 'REF' : lhsMeta.kind));
  const [rhsMode, setRhsMode] = React.useState(rhsMeta.kind === 'EMPTY' ? 'CONST' : (rhsMeta.kind === 'UNKNOWN' ? 'CONST' : rhsMeta.kind));

  const [lhsState, setLhsState] = React.useState({
    constValue: (lhsMeta.kind==='CONST') ? lhsMeta.value : 0,
    refValue: (lhsMeta.kind==='REF') ? lhsMeta.value : '',
    rawValue: (lhsMeta.kind==='RAW') ? lhsMeta.value : { field: 'volume', timeframe: '', lag: undefined },
  });
  const [rhsState, setRhsState] = React.useState({
    constValue: (rhsMeta.kind==='CONST') ? rhsMeta.value : 0,
    refValue: (rhsMeta.kind==='REF') ? rhsMeta.value : '',
    rawValue: (rhsMeta.kind==='RAW') ? rhsMeta.value : { field: 'volume', timeframe: '', lag: undefined },
  });

  // Sync outward when modes or internal states change
  React.useEffect(() => {
    const lhs = buildOperand(lhsMode, lhsState);
    const rhs = buildOperand(rhsMode, rhsState);
    onChange({ ...comparison, lhs, rhs });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lhsMode, rhsMode, lhsState, rhsState]);

  return (
    <div className="space-y-3">
      <div className="text-[11px] uppercase tracking-wide text-textSecondary">Comparison #{comparison.id}</div>
      <div className="grid grid-cols-1 gap-3">
        <OperandEditor
          label="LHS"
          mode={lhsMode}
          setMode={setLhsMode}
          state={lhsState}
          setState={setLhsState}
          availableSeries={availableSeries}
        />
        <div className="flex items-center gap-2">
          <label className="text-[10px] uppercase tracking-wide text-textSecondary w-10">Op</label>
          <select
            className="h-9 text-sm rounded-md border bg-background flex-1"
            value={comparison.operator}
            onChange={(e)=>onChange({ ...comparison, operator:e.target.value })}
          >
            {OPERATORS.map(op=>(<option key={op} value={op}>{op.replace(/_/g,' ')}</option>))}
          </select>
        </div>
        <OperandEditor
          label="RHS"
            mode={rhsMode}
            setMode={setRhsMode}
            state={rhsState}
            setState={setRhsState}
            availableSeries={availableSeries}
            defaultMode="CONST"
        />
      </div>
    </div>
  );
}

function OperandEditor({ label, mode, setMode, state, setState, availableSeries, defaultMode }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-[10px] uppercase tracking-wide text-textSecondary">{label}</label>
        <select
          className="h-7 text-[11px] rounded-md border bg-background"
          value={mode}
          onChange={(e)=>setMode(e.target.value)}
        >
          <option value="REF">Ref</option>
          <option value="RAW">Raw</option>
          <option value="CONST">Const</option>
        </select>
      </div>
      {mode === 'REF' && (
        <select
          className="h-9 text-sm rounded-md border bg-background w-full"
          value={state.refValue}
          onChange={(e)=>setState(s=>({...s, refValue:e.target.value}))}
        >
          <option value="" disabled>Select series</option>
          {availableSeries.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}
      {mode === 'CONST' && (
        <input
          type="number"
          className="h-9 text-sm rounded-md border bg-background w-full"
          value={state.constValue}
          onChange={(e)=>setState(s=>({...s, constValue: e.target.value === '' ? 0 : parseFloat(e.target.value) }))}
        />
      )}
      {mode === 'RAW' && (
        <div className="flex flex-wrap gap-2 items-center">
          <select
            className="h-8 text-xs rounded-md border bg-background"
            value={state.rawValue.field}
            onChange={(e)=>setState(s=>({...s, rawValue:{ ...s.rawValue, field:e.target.value }}))}
          >
            {['volume','close','open','high','low'].map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <input
            type="text"
            className="h-8 text-xs rounded-md border bg-background w-20"
            placeholder="TF (opt)"
            value={state.rawValue.timeframe || ''}
            onChange={(e)=>setState(s=>({...s, rawValue:{ ...s.rawValue, timeframe:e.target.value.trim() || undefined }}))}
          />
          <input
            type="number"
            min={0}
            className="h-8 text-xs rounded-md border bg-background w-16"
            placeholder="lag"
            value={state.rawValue.lag ?? ''}
            onChange={(e)=>{
              const val = e.target.value === '' ? undefined : Math.max(0, parseInt(e.target.value,10)||0);
              setState(s=>({...s, rawValue:{ ...s.rawValue, lag: val }}));
            }}
          />
        </div>
      )}
    </div>
  );
}
